import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getMeta, getOfflineUrl } from '../../offlineStorage';
import './StepPage.css';
import { tr, getLang } from '../../i18n';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
const BASE = API_URL.replace('/api', '');

// Russian plural for "балл": 1 балл, 2 балла, 5 баллов
export function pointsWord(n) {
  const lang = getLang();
  if (lang === 'en') return Math.abs(n) === 1 ? 'point' : 'points';
  if (lang === 'ar') return Math.abs(n) === 1 ? 'نقطة' : 'نقاط';
  const a = Math.abs(n) % 100;
  if (a >= 11 && a <= 14) return 'баллов';
  const b = a % 10;
  if (b === 1) return 'балл';
  if (b >= 2 && b <= 4) return 'балла';
  return 'баллов';
}

// Forgiving answer comparison: ignore case, extra spaces, ё/е, trailing punctuation
export function normalizeAnswer(s) {
  return (s || '')
    .toString()
    .toLowerCase()
    .replace(/ё/g, 'е')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[.!,;:]+$/, '')
    .trim();
}

export function renderFormattedText(text) {
  if (!text) return null;
  const links = [];
  let html = text
    .replace(/\[size:(\d+)\]([\s\S]*?)\[\/size\]/g, '<span style="font-size:$1px">$2</span>')
    .replace(/\[align:(left|center|right)\]([\s\S]*?)\[\/align\]/g, '<div style="text-align:$1">$2</div>')
    // Markdown links [text](url) — extract first so autolink below doesn't double-process them
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (_m, txt, url) => {
      const i = links.length;
      links.push(`<a href="${url}" target="_blank" rel="noopener noreferrer">${txt}</a>`);
      return `\x00LINK${i}\x00`;
    })
    // Auto-link plain http(s) URLs
    .replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
  html = html.replace(/\x00LINK(\d+)\x00/g, (_m, i) => links[parseInt(i)]);
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

// Hook to resolve offline URL
export function useOfflineUrl(url) {
  const [resolved, setResolved] = useState(url);
  useEffect(() => {
    if (!url) { setResolved(''); return; }
    getOfflineUrl(url).then(setResolved).catch(() => setResolved(url));
  }, [url]);
  return resolved;
}

const fmtTime = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;

// Self-contained audio player (one per audio track). Keeps ×1.5 speed + touch seek.
export function AudioPlayer({ rawUrl, title }) {
  const src = useOfflineUrl(rawUrl ? BASE + rawUrl : '');
  const audioRef = useRef(null);
  const barRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [seeking, setSeeking] = useState(false);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) { audioRef.current.pause(); }
    else {
      // pause other tracks
      document.querySelectorAll('audio').forEach(a => { if (a !== audioRef.current) a.pause(); });
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  const toggleSpeed = () => {
    const s = speed === 1 ? 1.5 : 1;
    setSpeed(s);
    if (audioRef.current) audioRef.current.playbackRate = s;
  };
  const onTimeUpdate = () => {
    if (!audioRef.current) return;
    setCurrentTime(audioRef.current.currentTime);
    setDuration(audioRef.current.duration || 0);
    setProgress((audioRef.current.currentTime / (audioRef.current.duration || 1)) * 100);
  };
  const onEnded = () => { setIsPlaying(false); setProgress(100); };
  const seek = (e) => {
    if (!audioRef.current?.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    audioRef.current.currentTime = pct * audioRef.current.duration;
  };

  return (
    <div className="audio-player">
      {title && <div className="audio-title">{title}</div>}
      <audio ref={audioRef} src={src} onTimeUpdate={onTimeUpdate} onEnded={onEnded} onLoadedMetadata={onTimeUpdate} />
      <div className="audio-bar" ref={barRef}
        onClick={seek}
        onTouchStart={(e) => { setSeeking(true); seek(e); }}
        onTouchMove={(e) => { if (seeking) seek(e); }}
        onTouchEnd={() => setSeeking(false)}>
        <div className="audio-bar-fill" style={{ width: `${progress}%` }} />
        <div className="audio-bar-dot" style={{ left: `${progress}%` }} />
      </div>
      <div className="audio-controls">
        <span className="audio-time">{fmtTime(currentTime)} / {fmtTime(duration)}</span>
        <button className="audio-play" onClick={togglePlay}>{isPlaying ? '⏸' : '▶'}</button>
        <button className="audio-speed" onClick={toggleSpeed}>×{speed}</button>
      </div>
    </div>
  );
}

// Fullscreen image viewer
export function FullscreenViewer({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex || 0);
  const touchStart = useRef(0);
  const touchEnd = useRef(0);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchMove = (e) => { touchEnd.current = e.touches[0].clientX; };
  const onTouchEnd = () => {
    const diff = touchStart.current - touchEnd.current;
    if (diff > 50 && index < images.length - 1) setIndex(index + 1);
    if (diff < -50 && index > 0) setIndex(index - 1);
  };

  return (
    <div className="fullscreen-viewer" onClick={onClose}>
      <button className="fullscreen-close" onClick={onClose}>✕</button>
      <div className="fullscreen-counter">{index + 1} / {images.length}</div>
      <div
        className="fullscreen-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={(e) => e.stopPropagation()}
      >
        {images.map((url, i) => (
          <div key={i} className="fullscreen-slide">
            <img src={url} alt="" className="fullscreen-img" />
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <div className="fullscreen-dots">
          {images.map((_, i) => (
            <div key={i} className={`fullscreen-dot ${i === index ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setIndex(i); }} />
          ))}
        </div>
      )}
    </div>
  );
}

// "Верю / Не верю" — swipeable statement card (one card of a quiz)
export function SwipeCard({ statement, image, onAnswer }) {
  const imgSrc = useOfflineUrl(image ? BASE + image : '');
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);
  const decided = useRef(false);

  const decide = (ans) => {
    if (decided.current) return;
    decided.current = true;
    setDx(ans === 'верю' ? 700 : -700);
    setTimeout(() => onAnswer(ans), 220);
  };
  const begin = (x) => { if (decided.current) return; startX.current = x; setDragging(true); };
  const move = (x) => { if (dragging) setDx(x - startX.current); };
  const end = () => {
    if (!dragging) return;
    setDragging(false);
    if (dx > 90) decide('верю');
    else if (dx < -90) decide('не верю');
    else setDx(0);
  };

  const rot = Math.max(-12, Math.min(12, dx / 18));
  const stamp = Math.min(Math.abs(dx) / 90, 1);

  return (
    <div className="swipe-card-wrap">
      <div className="swipe-card"
        style={{ transform: `translateX(${dx}px) rotate(${rot}deg)`, transition: dragging ? 'none' : 'transform .22s ease' }}
        onTouchStart={(e) => begin(e.touches[0].clientX)}
        onTouchMove={(e) => move(e.touches[0].clientX)}
        onTouchEnd={end}
        onMouseDown={(e) => begin(e.clientX)}
        onMouseMove={(e) => move(e.clientX)}
        onMouseUp={end}
        onMouseLeave={end}>
        <div className="swipe-stamp swipe-stamp-yes" style={{ opacity: dx > 0 ? stamp : 0 }}>{tr('ВЕРЮ')}</div>
        <div className="swipe-stamp swipe-stamp-no" style={{ opacity: dx < 0 ? stamp : 0 }}>{tr('НЕ ВЕРЮ')}</div>
        {imgSrc && <img className="swipe-card-img" src={imgSrc} alt="" draggable={false} />}
        <div className="swipe-card-text">{statement}</div>
      </div>
      <div className="swipe-card-hint">{tr('← смахните карточку или нажмите кнопку →')}</div>
      <div className="swipe-card-btns">
        <button className="swipe-card-btn no" onClick={() => decide('не верю')}>{tr('✕ Не верю')}</button>
        <button className="swipe-card-btn yes" onClick={() => decide('верю')}>{tr('✓ Верю')}</button>
      </div>
    </div>
  );
}

// Quiz of several "Верю / Не верю" cards
export function SwipeQuiz({ cards, onCard, onDone }) {
  const [i, setI] = useState(0);
  const [correct, setCorrect] = useState(0);
  const [done, setDone] = useState(false);
  const [feedback, setFeedback] = useState(null); // { ok, right, comment }

  if (done) {
    const all = correct === cards.length;
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">{all ? '🎉' : '👍'}</div>
        <div className="swipe-quiz-result-score">{tr('Верно {correct} из {total}', { correct, total: cards.length })}</div>
        <div className="swipe-quiz-result-sub">{all ? tr('Отлично! Все ответы верные') : tr('Хороший результат')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  if (feedback) {
    const isLast = i + 1 >= cards.length;
    const next = () => {
      const nc = correct + (feedback.ok ? 1 : 0);
      setCorrect(nc);
      setFeedback(null);
      if (isLast) setDone(true);
      else setI(i + 1);
    };
    return (
      <div className={`swipe-feedback ${feedback.ok ? 'ok' : 'bad'}`}>
        <div className="swipe-feedback-emoji">{feedback.ok ? '✅' : '❌'}</div>
        <div className="swipe-feedback-title">{feedback.ok ? tr('Верно!') : tr('Неверно')}</div>
        <div className="swipe-feedback-answer">{tr('Правильный ответ:')} <b>{feedback.right === 'верю' ? tr('Верю') : tr('Не верю')}</b></div>
        {feedback.comment ? <div className="swipe-feedback-comment">{feedback.comment}</div> : null}
        <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={next}>
          {isLast ? tr('Показать результат →') : tr('Следующая карточка →')}
        </button>
      </div>
    );
  }

  const card = cards[i];
  const handle = (given) => {
    const right = (card.answer === 'не верю') ? 'не верю' : 'верю';
    const ok = given === right;
    onCard(i, card, given, ok);
    setFeedback({ ok, right, comment: card.comment || '' });
  };

  return (
    <div>
      <div className="swipe-quiz-progress">{tr('Карточка {n} из {total}', { n: i + 1, total: cards.length })}</div>
      <SwipeCard key={i} statement={card.text} image={card.image} onAnswer={handle} />
    </div>
  );
}

// Tap-to-swap picture puzzle
export function PuzzleBoard({ image, size, onSolved }) {
  const src = useOfflineUrl(image ? BASE + image : '');
  const n = size * size;
  const [order, setOrder] = useState([]);
  const [sel, setSel] = useState(null);
  const [solved, setSolved] = useState(false);

  useEffect(() => {
    let arr;
    do {
      arr = Array.from({ length: n }, (_, i) => i);
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
    } while (arr.every((v, i) => v === i));
    setOrder(arr);
    setSel(null);
    setSolved(false);
  }, [n, image]);

  const tap = (pos) => {
    if (solved) return;
    if (sel === null) { setSel(pos); return; }
    if (sel === pos) { setSel(null); return; }
    const next = order.slice();
    [next[sel], next[pos]] = [next[pos], next[sel]];
    setSel(null);
    setOrder(next);
    if (next.every((v, i) => v === i)) setTimeout(() => setSolved(true), 350);
  };

  return (
    <div className="puzzle-wrap">
      {solved
        ? <div className="puzzle-solved-badge">{tr('🧩 Картинка собрана!')}</div>
        : <div className="puzzle-hint">{tr('Нажмите на две плитки, чтобы поменять их местами')}</div>}
      <div className={`puzzle-board ${solved ? 'solved' : ''}`} style={{ gridTemplateColumns: `repeat(${size}, 1fr)` }}>
        {order.map((tile, pos) => {
          const tx = (tile % size) / (size - 1) * 100;
          const ty = Math.floor(tile / size) / (size - 1) * 100;
          return (
            <div key={pos}
              className={`puzzle-tile ${sel === pos ? 'sel' : ''}`}
              onClick={() => tap(pos)}
              style={{
                backgroundImage: `url(${src})`,
                backgroundSize: `${size * 100}% ${size * 100}%`,
                backgroundPosition: `${tx}% ${ty}%`,
              }} />
          );
        })}
      </div>
      {solved && (
        <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onSolved}>{tr('Дальше →')}</button>
      )}
    </div>
  );
}

// One draggable/tappable chip (word and/or picture)
export function SortChip({ item, baseUrl, className, style, onPointerDown, onClick }) {
  const img = useOfflineUrl(item.image ? baseUrl + item.image : '');
  return (
    <div className={className} style={style} onPointerDown={onPointerDown} onClick={onClick}>
      {img && <img src={img} alt="" className="sort-chip-img" draggable={false} />}
      {item.text && <span>{item.text}</span>}
    </div>
  );
}

// Drag & drop sorting into categories
export function SortTask({ categories, items, baseUrl, maxAttempts, onItemCorrect, onSolved, onDone }) {
  const [placement, setPlacement] = useState(() => items.map(() => -1));
  const [locked, setLocked] = useState(() => items.map(() => false));
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null);
  const [result, setResult] = useState(null);
  const [solved, setSolved] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const dragRef = useRef(null);

  const allPlaced = items.every((_, i) => locked[i] || placement[i] !== -1);

  const place = (idx, dest) => {
    if (locked[idx]) return;
    setPlacement(p => { const n = p.slice(); n[idx] = dest; return n; });
    setSelected(null);
    setResult(null);
  };

  const onMove = (e) => {
    const d = dragRef.current; if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6) d.moved = true;
    if (d.moved) setDrag({ idx: d.idx, x: e.clientX, y: e.clientY });
  };
  const onUp = (e) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    const d = dragRef.current; dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.moved) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const binEl = el && el.closest('[data-bin]');
      const poolEl = el && el.closest('[data-pool]');
      if (binEl) place(d.idx, parseInt(binEl.dataset.bin));
      else if (poolEl) place(d.idx, -1);
    } else {
      setSelected(s => (s === d.idx ? null : d.idx));
    }
  };
  const onChipDown = (e, idx) => {
    if (locked[idx]) return;
    dragRef.current = { idx, startX: e.clientX, startY: e.clientY, moved: false };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const check = () => {
    let correct = 0;
    const nl = locked.slice();
    const np = placement.slice();
    items.forEach((it, i) => {
      if (nl[i]) { correct++; return; }
      if (np[i] === it.cat) { nl[i] = true; correct++; onItemCorrect(i); }
      else { np[i] = -1; }
    });
    setLocked(nl);
    setPlacement(np);
    if (nl.every(Boolean)) { setSolved(true); onSolved(); return; }
    const used = attempts + 1;
    setAttempts(used);
    if (maxAttempts > 0 && used >= maxAttempts) {
      setExhausted(true);
      setResult({ text: tr('Верно разложено: {correct} из {total}. Попытки закончились — нажмите «Дальше».', { correct, total: items.length }) });
    } else if (maxAttempts > 0) {
      setResult({ text: tr('Верно разложено: {correct} из {total}. Осталось попыток: {left}. Неверные вернулись наверх.', { correct, total: items.length, left: maxAttempts - used }) });
    } else {
      setResult({ text: correct < items.length
        ? tr('Верно разложено: {correct} из {total}. Неверные вернулись наверх — попробуйте ещё раз.', { correct, total: items.length })
        : tr('Верно разложено: {correct} из {total}.', { correct, total: items.length }) });
    }
  };

  if (solved) {
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">🎉</div>
        <div className="swipe-quiz-result-score">{tr('Готово!')}</div>
        <div className="swipe-quiz-result-sub">{tr('Всё разложено верно')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  return (
    <div className="sort-task">
      <div className="sort-hint">{tr('Перетащите элемент в нужную категорию (или нажмите на элемент, затем на категорию)')}</div>
      <div className="sort-pool" data-pool onClick={() => { if (selected != null) place(selected, -1); }}>
        {items.map((it, i) => (placement[i] === -1 && !locked[i]) && (
          <SortChip key={i} item={it} baseUrl={baseUrl}
            className={`sort-chip ${selected === i ? 'sel' : ''}`}
            onPointerDown={(e) => onChipDown(e, i)}
            onClick={(e) => e.stopPropagation()} />
        ))}
        {allPlaced && <div className="sort-pool-empty">{tr('Все элементы разложены — нажмите «Проверить»')}</div>}
      </div>
      <div className="sort-bins">
        {categories.map((c, ci) => (
          <div key={ci} className="sort-bin" data-bin={ci} onClick={() => { if (selected != null) place(selected, ci); }}>
            <div className="sort-bin-title">{c}</div>
            <div className="sort-bin-body">
              {items.map((it, i) => placement[i] === ci && (
                <SortChip key={i} item={it} baseUrl={baseUrl}
                  className={`sort-chip ${locked[i] ? 'ok' : ''} ${selected === i ? 'sel' : ''}`}
                  onPointerDown={(e) => onChipDown(e, i)}
                  onClick={(e) => e.stopPropagation()} />
              ))}
            </div>
          </div>
        ))}
      </div>
      {result && <div className="sort-result">{result.text}</div>}
      {exhausted
        ? <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Дальше →')}</button>
        : <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} disabled={!allPlaced} onClick={check}>{tr('Проверить')}</button>}
      {drag && (
        <SortChip item={items[drag.idx]} baseUrl={baseUrl} className="sort-chip sort-ghost"
          style={{ left: drag.x, top: drag.y }} />
      )}
    </div>
  );
}

// "Собери набор" — drag correct items into a container
export function DishTask({ containerTitle, items, baseUrl, maxAttempts, onSolved, onDone }) {
  const [inPlate, setInPlate] = useState(() => items.map(() => false));
  const [selected, setSelected] = useState(null);
  const [drag, setDrag] = useState(null);
  const [result, setResult] = useState(null);
  const [solved, setSolved] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [exhausted, setExhausted] = useState(false);
  const dragRef = useRef(null);

  const move = (idx, toPlate) => {
    setInPlate(p => { const n = p.slice(); n[idx] = toPlate; return n; });
    setSelected(null);
    setResult(null);
  };
  const onMove = (e) => {
    const d = dragRef.current; if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6) d.moved = true;
    if (d.moved) setDrag({ idx: d.idx, x: e.clientX, y: e.clientY });
  };
  const onUp = (e) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    const d = dragRef.current; dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.moved) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      if (el && el.closest('[data-plate]')) move(d.idx, true);
      else if (el && el.closest('[data-pool]')) move(d.idx, false);
    } else {
      setSelected(s => (s === d.idx ? null : d.idx));
    }
  };
  const onChipDown = (e, idx) => {
    dragRef.current = { idx, startX: e.clientX, startY: e.clientY, moved: false };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const check = () => {
    const ok = items.every((it, i) => !!inPlate[i] === !!it.correct);
    if (ok) { setSolved(true); onSolved(); return; }
    const used = attempts + 1;
    setAttempts(used);
    if (maxAttempts > 0 && used >= maxAttempts) {
      setExhausted(true);
      setResult({ text: tr('Не совсем верно. Попытки закончились — нажмите «Дальше».') });
    } else if (maxAttempts > 0) {
      setResult({ text: tr('Не совсем верно. Осталось попыток: {left}.', { left: maxAttempts - used }) });
    } else {
      setResult({ text: tr('Не совсем верно — проверьте состав и попробуйте ещё раз.') });
    }
  };

  if (solved) {
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">🎉</div>
        <div className="swipe-quiz-result-score">{tr('Готово!')}</div>
        <div className="swipe-quiz-result-sub">{tr('Набор собран верно')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  const placedCount = inPlate.filter(Boolean).length;

  return (
    <div className="sort-task">
      <div className="sort-hint">{tr('Перетащите нужные элементы в «{name}» (или нажмите на элемент, затем на контейнер)', { name: containerTitle || tr('контейнер') })}</div>
      <div className="sort-pool" data-pool onClick={() => { if (selected != null) move(selected, false); }}>
        {items.map((it, i) => (!inPlate[i]) && (
          <SortChip key={i} item={it} baseUrl={baseUrl}
            className={`sort-chip ${selected === i ? 'sel' : ''}`}
            onPointerDown={(e) => onChipDown(e, i)}
            onClick={(e) => e.stopPropagation()} />
        ))}
        {placedCount === items.length && <div className="sort-pool-empty">{tr('Все элементы перенесены')}</div>}
      </div>
      <div className="sort-bin" data-plate onClick={() => { if (selected != null) move(selected, true); }}>
        <div className="sort-bin-title">🍽 {containerTitle || tr('Контейнер')}</div>
        <div className="sort-bin-body" style={{ minHeight: 70 }}>
          {items.map((it, i) => inPlate[i] && (
            <SortChip key={i} item={it} baseUrl={baseUrl}
              className={`sort-chip ${selected === i ? 'sel' : ''}`}
              onPointerDown={(e) => onChipDown(e, i)}
              onClick={(e) => e.stopPropagation()} />
          ))}
          {placedCount === 0 && <div className="sort-pool-empty">{tr('Перетащите сюда нужные элементы')}</div>}
        </div>
      </div>
      {result && <div className="sort-result">{result.text}</div>}
      {exhausted
        ? <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Дальше →')}</button>
        : <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} disabled={placedCount === 0} onClick={check}>{tr('Проверить')}</button>}
      {drag && (
        <SortChip item={items[drag.idx]} baseUrl={baseUrl} className="sort-chip sort-ghost"
          style={{ left: drag.x, top: drag.y }} />
      )}
    </div>
  );
}

// "Составь фразу" — drag word/picture cards from the pool into the frame in the correct order
export function PhraseTask({ cards, extras, baseUrl, maxAttempts, onSolved, onDone, onImageTap }) {
  const all = useMemo(() => [...(cards || []), ...(extras || [])], [cards, extras]);
  const correctLen = (cards || []).length;
  const correctSig = useMemo(() => (cards || []).map(c => `${(c && c.text) || ''}|${(c && c.image) || ''}`), [cards]);
  const [shuffled, setShuffled] = useState([]);
  const [placed, setPlaced] = useState([]);
  const [drag, setDrag] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [result, setResult] = useState(null);
  const [solved, setSolved] = useState(false);
  const [exhausted, setExhausted] = useState(false);
  const dragRef = useRef(null);

  useEffect(() => {
    const a = all.map((_, i) => i);
    for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    setShuffled(a);
    setPlaced([]);
  }, [all]);

  const poolIdx = shuffled.filter(i => !placed.includes(i));

  const addToFrame = (idx) => {
    if (exhausted) return;
    setPlaced(p => p.includes(idx) ? p : [...p, idx]);
    setResult(null);
  };
  const removeFromFrame = (pos) => {
    if (exhausted) return;
    setPlaced(p => p.filter((_, i) => i !== pos));
    setResult(null);
  };
  const moveInFrame = (pos, dir) => {
    if (exhausted) return;
    const j = pos + dir;
    if (j < 0 || j >= placed.length) return;
    setPlaced(p => { const n = p.slice(); [n[pos], n[j]] = [n[j], n[pos]]; return n; });
    setResult(null);
  };

  const onMove = (e) => {
    const d = dragRef.current; if (!d) return;
    if (!d.moved && Math.hypot(e.clientX - d.startX, e.clientY - d.startY) > 6) d.moved = true;
    if (d.moved) setDrag({ idx: d.idx, x: e.clientX, y: e.clientY });
  };
  const onUp = (e) => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    const d = dragRef.current; dragRef.current = null;
    setDrag(null);
    if (!d) return;
    if (d.moved) {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const frame = el && el.closest('[data-frame]');
      const pool = el && el.closest('[data-pool]');
      if (frame) addToFrame(d.idx);
      else if (pool) { const pos = placed.indexOf(d.idx); if (pos !== -1) removeFromFrame(pos); }
    } else {
      const pos = placed.indexOf(d.idx);
      if (pos !== -1) removeFromFrame(pos);
      else addToFrame(d.idx);
    }
  };
  const onChipDown = (e, idx) => {
    if (exhausted) return;
    dragRef.current = { idx, startX: e.clientX, startY: e.clientY, moved: false };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const check = () => {
    if (exhausted) return;
    if (placed.length !== correctLen) {
      setResult({ text: tr(correctLen === 1 ? 'Во фразе должно быть {n} элемент. Сейчас: {now}.' : (correctLen < 5 ? 'Во фразе должно быть {n} элемента. Сейчас: {now}.' : 'Во фразе должно быть {n} элементов. Сейчас: {now}.'), { n: correctLen, now: placed.length }) });
      return;
    }
    const ok = placed.every((idx, i) => `${(all[idx] && all[idx].text) || ''}|${(all[idx] && all[idx].image) || ''}` === correctSig[i]);
    if (ok) { setSolved(true); onSolved(); return; }
    const used = attempts + 1;
    setAttempts(used);
    if (maxAttempts > 0 && used >= maxAttempts) {
      setExhausted(true);
      setResult({ text: tr('Не совсем верно. Попытки закончились — нажмите «Дальше».') });
    } else if (maxAttempts > 0) {
      setResult({ text: tr('Не совсем верно. Осталось попыток: {left}.', { left: maxAttempts - used }) });
    } else {
      setResult({ text: tr('Не совсем верно — попробуйте ещё раз.') });
    }
  };

  if (solved) {
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">🎉</div>
        <div className="swipe-quiz-result-score">{tr('Верно!')}</div>
        <div className="swipe-quiz-result-sub">{tr('Фраза собрана правильно')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  return (
    <div className="sort-task phrase-task">
      <div className="sort-hint">{tr('Нажмите на карточки в нужном порядке (или перетащите их в рамку). Стрелками ← → можно поменять порядок.')}</div>
      <div className="sort-pool" data-pool>
        {poolIdx.map(idx => (
          <SortChip key={idx} item={all[idx]} baseUrl={baseUrl}
            className="sort-chip"
            onPointerDown={(e) => onChipDown(e, idx)}
            onClick={(e) => e.stopPropagation()} />
        ))}
        {poolIdx.length === 0 && <div className="sort-pool-empty">{tr('Все карточки в рамке — нажмите «Проверить»')}</div>}
      </div>
      <div className="phrase-frame-label">{tr('Ваша фраза:')}</div>
      <div className="phrase-frame" data-frame>
        {placed.length === 0 && <div className="sort-pool-empty">{tr('Перетащите сюда карточки (или нажмите на них сверху)')}</div>}
        {placed.map((idx, pos) => (
          <div key={`${idx}-${pos}`} className="phrase-slot">
            <span className="phrase-slot-num">{pos + 1}</span>
            <SortChip item={all[idx]} baseUrl={baseUrl} className="sort-chip phrase-slot-chip"
              onPointerDown={(e) => onChipDown(e, idx)}
              onClick={(e) => e.stopPropagation()} />
            <div className="phrase-slot-btns">
              <button type="button" disabled={pos === 0 || exhausted} onClick={() => moveInFrame(pos, -1)} aria-label={tr('влево')}>←</button>
              <button type="button" disabled={pos === placed.length - 1 || exhausted} onClick={() => moveInFrame(pos, 1)} aria-label={tr('вправо')}>→</button>
              <button type="button" className="phrase-slot-x" disabled={exhausted} onClick={() => removeFromFrame(pos)} aria-label={tr('убрать')}>×</button>
            </div>
          </div>
        ))}
      </div>
      {result && <div className="sort-result">{result.text}</div>}
      {exhausted
        ? <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Дальше →')}</button>
        : <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} disabled={placed.length === 0} onClick={check}>{tr('Проверить')}</button>}
      {drag && (
        <SortChip item={all[drag.idx]} baseUrl={baseUrl} className="sort-chip sort-ghost"
          style={{ left: drag.x, top: drag.y }} />
      )}
    </div>
  );
}

// One row of the ordering task
export function OrderCell({ item, baseUrl, onImageTap }) {
  const img = useOfflineUrl(item.image ? baseUrl + item.image : '');
  return (
    <div className="order-cell">
      {img && <img src={img} alt="" className="order-cell-img"
        onClick={(e) => { e.stopPropagation(); onImageTap && onImageTap(img); }} />}
      {item.text && <span>{item.text}</span>}
    </div>
  );
}

// "Расставить по порядку"
export function OrderTask({ items, baseUrl, maxAttempts, onSolved, onDone, onImageTap }) {
  const [order, setOrder] = useState([]);
  const [attempts, setAttempts] = useState(0);
  const [msg, setMsg] = useState('');
  const [solved, setSolved] = useState(false);
  const [exhausted, setExhausted] = useState(false);

  useEffect(() => {
    let a;
    do {
      a = items.map((_, i) => i);
      for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; }
    } while (items.length > 1 && a.every((v, i) => v === i));
    setOrder(a);
  }, [items]);

  const move = (pos, dir) => {
    if (solved || exhausted) return;
    const j = pos + dir;
    if (j < 0 || j >= order.length) return;
    const a = order.slice(); [a[pos], a[j]] = [a[j], a[pos]]; setOrder(a); setMsg('');
  };

  const check = () => {
    if (order.every((v, i) => v === i)) { setSolved(true); onSolved(); return; }
    const used = attempts + 1; setAttempts(used);
    if (maxAttempts > 0 && used >= maxAttempts) { setExhausted(true); setMsg(tr('Попытки закончились. Нажмите «Дальше».')); }
    else setMsg(maxAttempts > 0 ? tr('Неверный порядок. Осталось попыток: {left}', { left: maxAttempts - used }) : tr('Неверный порядок, попробуйте ещё раз.'));
  };

  if (solved) {
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">🎉</div>
        <div className="swipe-quiz-result-score">{tr('Верно!')}</div>
        <div className="swipe-quiz-result-sub">{tr('Порядок правильный')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  return (
    <div className="order-task">
      <div className="sort-hint">{tr('Расставьте элементы кнопками ↑ ↓. Нажмите на картинку, чтобы увеличить')}</div>
      <div className="order-list">
        {order.map((idx, pos) => (
          <div key={idx} className="order-row">
            <span className="order-num">{pos + 1}</span>
            <div className="order-content"><OrderCell item={items[idx]} baseUrl={baseUrl} onImageTap={onImageTap} /></div>
            <div className="order-btns">
              <button disabled={pos === 0 || exhausted} onClick={() => move(pos, -1)}>↑</button>
              <button disabled={pos === order.length - 1 || exhausted} onClick={() => move(pos, 1)}>↓</button>
            </div>
          </div>
        ))}
      </div>
      {msg && <div className="sort-result">{msg}</div>}
      {exhausted
        ? <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Дальше →')}</button>
        : <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={check}>{tr('Проверить')}</button>}
    </div>
  );
}

// "Повтори цвета" — Simon-style memory sequence
const MEM_COLORS = [
  { bg: '#e8533d', name: 'красный' },
  { bg: '#3A7BD5', name: 'синий' },
  { bg: '#2e9e5b', name: 'зелёный' },
];
// Postcard constructor (drag & resize, keeps the uploaded background ratio)
const PC_FONTS = [
  { k: 'sans', n: 'Обычный', f: '-apple-system, system-ui, Arial, sans-serif' },
  { k: 'serif', n: 'Классика', f: 'Georgia, "Times New Roman", serif' },
  { k: 'round', n: 'Уютный', f: '"Trebuchet MS", "Comic Sans MS", sans-serif' },
  { k: 'mono', n: 'Печатная', f: '"Courier New", monospace' },
];
const PC_COLORS = ['#ffffff', '#1a1a1a', '#E0533D', '#F2A93B', '#2DBDA8', '#3A7BD5', '#C2185B'];
const pcClamp = (v, a, b) => Math.max(a, Math.min(b, v));

export function PostcardMakerNew({ backgrounds, stickers, baseUrl, onDone }) {
  const [bgIdx, setBgIdx] = useState(0);
  const [items, setItems] = useState([]); // {id, src, x, y, scale}
  const [txt, setTxt] = useState({ content: '', font: 'sans', color: '#ffffff', x: 50, y: 82, scale: 1 });
  const [sel, setSel] = useState(null); // {t:'s', id} | {t:'t'} | null
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null); // { url, file }
  const frameRef = useRef(null);
  const drag = useRef(null);
  const idRef = useRef(1);

  const bg = backgrounds[bgIdx] || backgrounds[0] || '';
  const fontOf = (k) => (PC_FONTS.find(f => f.k === k) || PC_FONTS[0]).f;

  const addSticker = (src) => {
    if (items.length >= 12) return;
    const id = idRef.current++;
    setItems(a => [...a, { id, src, x: 50, y: 45, scale: 1 }]);
    setSel({ t: 's', id });
  };

  const applyPos = (t, nx, ny) => {
    if (t.t === 't') setTxt(p => ({ ...p, x: nx, y: ny }));
    else setItems(a => a.map(it => it.id === t.id ? { ...it, x: nx, y: ny } : it));
  };
  const applyScale = (t, s) => {
    if (t.t === 't') setTxt(p => ({ ...p, scale: s }));
    else setItems(a => a.map(it => it.id === t.id ? { ...it, scale: s } : it));
  };

  const onMove = (e) => {
    const d = drag.current; if (!d) return;
    const fr = frameRef.current.getBoundingClientRect();
    if (d.mode === 'move') {
      const nx = pcClamp(d.ox + (e.clientX - d.sx) / fr.width * 100, 2, 98);
      const ny = pcClamp(d.oy + (e.clientY - d.sy) / fr.height * 100, 2, 98);
      applyPos(d.target, nx, ny);
    } else {
      const dist = Math.hypot(e.clientX - d.cx, e.clientY - d.cy);
      applyScale(d.target, pcClamp(d.s0 * dist / d.d0, 0.3, 6));
    }
  };
  const onUp = () => {
    window.removeEventListener('pointermove', onMove);
    window.removeEventListener('pointerup', onUp);
    drag.current = null;
  };
  const win = () => {
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const curEl = (t) => (t.t === 't' ? txt : items.find(i => i.id === t.id));
  const startMove = (e, target) => {
    e.stopPropagation();
    setSel(target);
    const el = curEl(target); if (!el) return;
    drag.current = { mode: 'move', target, sx: e.clientX, sy: e.clientY, ox: el.x, oy: el.y };
    win();
  };
  const startResize = (e) => {
    e.stopPropagation();
    if (!sel) return;
    const el = curEl(sel); if (!el) return;
    const fr = frameRef.current.getBoundingClientRect();
    const cx = fr.left + el.x / 100 * fr.width;
    const cy = fr.top + el.y / 100 * fr.height;
    const d0 = Math.hypot(e.clientX - cx, e.clientY - cy) || 1;
    drag.current = { mode: 'resize', target: sel, cx, cy, d0, s0: el.scale };
    win();
  };

  const loadImg = (url) => new Promise((res) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = url;
  });

  const download = async () => {
    setSaving(true);
    const bgim = bg ? await loadImg(baseUrl + bg) : null;
    let W = bgim ? bgim.naturalWidth : 1000;
    let H = bgim ? bgim.naturalHeight : 1400;
    const k = Math.min(1, 1600 / Math.max(W, H));
    W = Math.round(W * k); H = Math.round(H * k);
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#e9edf2'; ctx.fillRect(0, 0, W, H);
    if (bgim) ctx.drawImage(bgim, 0, 0, W, H); // полный фон, без обрезки
    for (const it of items) {
      const sim = await loadImg(baseUrl + it.src);
      if (!sim) continue;
      const base = Math.min(W, H) * 0.22 * it.scale;
      const ratio = (sim.width && sim.height) ? sim.width / sim.height : 1;
      let sw = base, sh = base;
      if (ratio > 1) sh = base / ratio; else sw = base * ratio;
      ctx.drawImage(sim, it.x / 100 * W - sw / 2, it.y / 100 * H - sh / 2, sw, sh);
    }
    if (txt.content.trim()) {
      const fpx = Math.min(W, H) * 0.07 * txt.scale;
      ctx.font = `800 ${fpx}px ${fontOf(txt.font)}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const maxW = W * 0.9;
      const words = txt.content.trim().split(/\s+/);
      const lines = []; let cur = '';
      words.forEach(w => {
        const tt = cur ? cur + ' ' + w : w;
        if (ctx.measureText(tt).width > maxW && cur) { lines.push(cur); cur = w; } else cur = tt;
      });
      if (cur) lines.push(cur);
      const lh = fpx * 1.22;
      let yy = txt.y / 100 * H - (lines.length - 1) * lh / 2;
      const light = /^#f|^#e|ffffff/i.test(txt.color) || txt.color === '#ffffff';
      lines.forEach(ln => {
        ctx.lineWidth = fpx * 0.16;
        ctx.strokeStyle = light ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.6)';
        ctx.strokeText(ln, txt.x / 100 * W, yy);
        ctx.fillStyle = txt.color;
        ctx.fillText(ln, txt.x / 100 * W, yy);
        yy += lh;
      });
    }
    cv.toBlob((blob) => {
      setSaving(false);
      if (!blob) return;
      const file = new File([blob], 'otkrytka.png', { type: 'image/png' });
      const url = URL.createObjectURL(blob);
      setResult({ url, file });
      try {
        if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
          navigator.share({ files: [file], title: tr('Открытка') }).catch(() => {});
          return;
        }
      } catch (e) { /* ignore */ }
      const a = document.createElement('a');
      a.href = url; a.download = 'otkrytka.png';
      document.body.appendChild(a); a.click(); a.remove();
    }, 'image/png');
  };

  const shareAgain = () => {
    if (!result) return;
    try {
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [result.file] })) {
        navigator.share({ files: [result.file], title: tr('Открытка') }).catch(() => {});
        return;
      }
    } catch (e) { /* ignore */ }
    const a = document.createElement('a');
    a.href = result.url; a.download = 'otkrytka.png';
    document.body.appendChild(a); a.click(); a.remove();
  };
  const closeResult = () => {
    if (result) { try { URL.revokeObjectURL(result.url); } catch (e) {} }
    setResult(null);
  };

  const elStyle = (el) => ({ left: el.x + '%', top: el.y + '%', transform: `translate(-50%,-50%) scale(${el.scale})` });

  return (
    <div className="pc-wrap">
      <div className="pc-frame" ref={frameRef} onPointerDown={() => setSel(null)}>
        {bg ? <img className="pc-bg" src={baseUrl + bg} alt="" draggable={false} /> : <div className="pc-bg pc-bg-empty">{tr('Выберите фон')}</div>}
        {items.map(it => {
          const s = sel && sel.t === 's' && sel.id === it.id;
          return (
            <div key={it.id} className={`pc-el ${s ? 'sel' : ''}`} style={elStyle(it)}
              onPointerDown={(e) => startMove(e, { t: 's', id: it.id })}>
              <img src={baseUrl + it.src} className="pc-el-img" draggable={false} alt="" />
              {s && <span className="pc-del" onPointerDown={(e) => { e.stopPropagation(); setItems(a => a.filter(i => i.id !== it.id)); setSel(null); }}>✕</span>}
              {s && <span className="pc-handle" onPointerDown={startResize} />}
            </div>
          );
        })}
        {txt.content.trim() && (
          <div className={`pc-el pc-text-el ${sel && sel.t === 't' ? 'sel' : ''}`} style={elStyle(txt)}
            onPointerDown={(e) => startMove(e, { t: 't' })}>
            <span className="pc-txt" style={{ fontFamily: fontOf(txt.font), color: txt.color }}>{txt.content}</span>
            {sel && sel.t === 't' && <span className="pc-del" onPointerDown={(e) => { e.stopPropagation(); setTxt(p => ({ ...p, content: '' })); setSel(null); }}>✕</span>}
            {sel && sel.t === 't' && <span className="pc-handle" onPointerDown={startResize} />}
          </div>
        )}
      </div>

      {backgrounds.length > 1 && (
        <div className="pc-row">
          {backgrounds.map((b, i) => (
            <img key={i} src={baseUrl + b} alt="" draggable={false}
              className={`pc-thumb ${i === bgIdx ? 'sel' : ''}`} onClick={() => setBgIdx(i)} />
          ))}
        </div>
      )}
      {stickers.length > 0 && (
        <div className="pc-row">
          {stickers.map((s, i) => (
            <img key={i} src={baseUrl + s} alt="" draggable={false}
              className="pc-pick" onClick={() => addSticker(s)} />
          ))}
        </div>
      )}

      <input className="auth-input" style={{ fontSize: 16, marginTop: 10 }} maxLength={120}
        placeholder={tr('Текст на открытке')} value={txt.content}
        onChange={(e) => setTxt(p => ({ ...p, content: e.target.value }))} />
      <div className="pc-tools">
        {PC_FONTS.map(f => (
          <button key={f.k} type="button"
            className={`pc-font ${txt.font === f.k ? 'sel' : ''}`}
            style={{ fontFamily: f.f }}
            onClick={() => setTxt(p => ({ ...p, font: f.k }))}>{tr(f.n)}</button>
        ))}
      </div>
      <div className="pc-tools">
        {PC_COLORS.map(c => (
          <button key={c} type="button"
            className={`pc-color ${txt.color === c ? 'sel' : ''}`}
            style={{ background: c }}
            onClick={() => setTxt(p => ({ ...p, color: c }))} />
        ))}
      </div>
      <div className="pc-hint">{tr('Нажмите наклейку — добавится. Перетаскивайте пальцем; уголок ▢ — меняет размер. Текст так же двигается и тянется.')}</div>
      <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 10 }} disabled={saving} onClick={download}>
        {saving ? tr('Готовим…') : tr('⬇ Сохранить открытку')}
      </button>
      <button className="swipe-card-btn" style={{ width: '100%', marginTop: 8 }} onClick={onDone}>{tr('Далее →')}</button>

      {result && (
        <div className="pc-result-overlay" onClick={closeResult}>
          <div className="pc-result-box" onClick={(e) => e.stopPropagation()}>
            <div className="pc-result-title">{tr('Ваша открытка готова! 🎉')}</div>
            <img src={result.url} alt={tr('Открытка')} className="pc-result-img" />
            <div className="pc-result-hint">{tr('Чтобы сохранить: задержите палец на картинке и выберите «Сохранить изображение» — или нажмите кнопку ниже.')}</div>
            <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 10 }} onClick={shareAgain}>{tr('📤 Сохранить / Поделиться')}</button>
            <button className="swipe-card-btn" style={{ width: '100%', marginTop: 8 }} onClick={closeResult}>{tr('Закрыть')}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// (старая версия конструктора — не используется)
export function PostcardMaker({ backgrounds, stickers, baseUrl, onDone }) {
  const [bgIdx, setBgIdx] = useState(0);
  const [items, setItems] = useState([]);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const bg = backgrounds[bgIdx] || backgrounds[0] || '';

  const addSticker = (s) => {
    if (items.length >= 10) return;
    setItems(a => [...a, { src: s, x: 15 + Math.random() * 70, y: 15 + Math.random() * 55 }]);
  };
  const loadImg = (url) => new Promise((res) => {
    const im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = () => res(im);
    im.onerror = () => res(null);
    im.src = url;
  });
  const download = async () => {
    setSaving(true);
    const W = 1200, H = 800;
    const cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    const ctx = cv.getContext('2d');
    ctx.fillStyle = '#dfe6ee'; ctx.fillRect(0, 0, W, H);
    const bgim = bg ? await loadImg(baseUrl + bg) : null;
    if (bgim) {
      const r = Math.max(W / bgim.width, H / bgim.height);
      const dw = bgim.width * r, dh = bgim.height * r;
      ctx.drawImage(bgim, (W - dw) / 2, (H - dh) / 2, dw, dh);
    }
    for (const it of items) {
      const sim = await loadImg(baseUrl + it.src);
      if (!sim) continue;
      const size = W * 0.17;
      const ratio = (sim.width && sim.height) ? sim.width / sim.height : 1;
      let sw = size, sh = size;
      if (ratio > 1) sh = size / ratio; else sw = size * ratio;
      ctx.drawImage(sim, it.x / 100 * W - sw / 2, it.y / 100 * H - sh / 2, sw, sh);
    }
    if (text.trim()) {
      ctx.font = '700 46px -apple-system, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';
      const maxW = W * 0.86;
      const words = text.trim().split(/\s+/);
      const lines = []; let cur = '';
      words.forEach(w => {
        const t = cur ? cur + ' ' + w : w;
        if (ctx.measureText(t).width > maxW && cur) { lines.push(cur); cur = w; } else cur = t;
      });
      if (cur) lines.push(cur);
      const lh = 56;
      let yy = H - 44 - (lines.length - 1) * lh;
      lines.forEach(ln => {
        ctx.lineWidth = 8; ctx.strokeStyle = 'rgba(0,0,0,0.45)'; ctx.strokeText(ln, W / 2, yy);
        ctx.fillStyle = '#fff'; ctx.fillText(ln, W / 2, yy);
        yy += lh;
      });
    }
    cv.toBlob((blob) => {
      if (blob) {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'otkrytka.png';
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1500);
      }
      setSaving(false);
    }, 'image/png');
  };

  return (
    <div className="pc-wrap">
      <div className="pc-frame" style={{ backgroundImage: bg ? `url(${baseUrl + bg})` : 'none' }}>
        {items.map((it, i) => (
          <img key={i} src={baseUrl + it.src} alt="" className="pc-sticker"
            style={{ left: it.x + '%', top: it.y + '%' }}
            onClick={() => setItems(a => a.filter((_, x) => x !== i))} draggable={false} />
        ))}
        {text.trim() && <div className="pc-text">{text}</div>}
      </div>

      {backgrounds.length > 1 && (
        <div className="pc-row">
          {backgrounds.map((b, i) => (
            <img key={i} src={baseUrl + b} alt="" draggable={false}
              className={`pc-thumb ${i === bgIdx ? 'sel' : ''}`} onClick={() => setBgIdx(i)} />
          ))}
        </div>
      )}
      {stickers.length > 0 && (
        <div className="pc-row">
          {stickers.map((s, i) => (
            <img key={i} src={baseUrl + s} alt="" draggable={false}
              className="pc-pick" onClick={() => addSticker(s)} />
          ))}
        </div>
      )}
      <input className="auth-input" style={{ fontSize: 16, marginTop: 10 }} maxLength={120}
        placeholder={tr('Подпись на открытке')} value={text} onChange={(e) => setText(e.target.value)} />
      <div className="pc-hint">{tr('Нажмите наклейку — она появится на открытке. Нажмите наклейку на открытке — уберёте.')}</div>
      <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 10 }} disabled={saving} onClick={download}>
        {saving ? tr('Готовим…') : tr('⬇ Скачать открытку')}
      </button>
      <button className="swipe-card-btn" style={{ width: '100%', marginTop: 8 }} onClick={onDone}>{tr('Далее →')}</button>
    </div>
  );
}

// Memory "match the pairs" game
export function PairFace({ text, image, baseUrl }) {
  const img = useOfflineUrl(image ? baseUrl + image : '');
  return (
    <div className="pair-face">
      {img && <img src={img} alt="" className="pair-face-img" draggable={false} />}
      {text && <span className="pair-face-text">{text}</span>}
    </div>
  );
}

export function PairsTask({ pairs, baseUrl, backImage, onMatched, onSolved, onDone }) {
  const backSrc = useOfflineUrl(backImage ? baseUrl + backImage : '');
  const [cards] = useState(() => {
    const list = [];
    pairs.forEach((p, idx) => {
      list.push({ pid: idx, t: p.a && p.a.text, im: p.a && p.a.image });
      list.push({ pid: idx, t: p.b && p.b.text, im: p.b && p.b.image });
    });
    for (let i = list.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  });
  const [flipped, setFlipped] = useState([]);
  const [matched, setMatched] = useState([]);
  const [busy, setBusy] = useState(false);
  const [solved, setSolved] = useState(false);

  const cols = cards.length <= 4 ? 2 : cards.length <= 12 ? 3 : 4;

  const tap = (i) => {
    const c = cards[i];
    if (busy || solved || matched.includes(c.pid) || flipped.includes(i) || flipped.length === 2) return;
    const nf = [...flipped, i];
    setFlipped(nf);
    if (nf.length === 2) {
      const [x, y] = nf;
      if (cards[x].pid === cards[y].pid) {
        const pid = cards[x].pid;
        setTimeout(() => {
          const nm = [...matched, pid];
          setMatched(nm);
          setFlipped([]);
          onMatched(pid);
          if (nm.length === pairs.length) { setSolved(true); onSolved(); }
        }, 450);
      } else {
        setBusy(true);
        setTimeout(() => { setFlipped([]); setBusy(false); }, 950);
      }
    }
  };

  if (solved) {
    return (
      <div className="swipe-quiz-result">
        <div className="swipe-quiz-result-emoji">🎉</div>
        <div className="swipe-quiz-result-score">{tr('Все пары найдены!')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%' }} onClick={onDone}>{tr('Далее →')}</button>
      </div>
    );
  }

  return (
    <div className="pairs-task">
      <div className="sort-hint">{tr('Открывайте по две карточки и ищите совпадения')}</div>
      <div className="pairs-grid" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
        {cards.map((c, i) => {
          const open = matched.includes(c.pid) || flipped.includes(i);
          return (
            <div key={i}
              className={`pair-card ${open ? 'open' : ''} ${matched.includes(c.pid) ? 'matched' : ''}`}
              onClick={() => tap(i)}>
              {open
                ? <PairFace text={c.t} image={c.im} baseUrl={baseUrl} />
                : (backSrc ? <img src={backSrc} alt="" className="pair-back-img" draggable={false} /> : <span className="pair-back">?</span>)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function MemorySequence({ length, revealImage, revealText, maxAttempts, onSolved, onDone }) {
  const revealSrc = useOfflineUrl(revealImage ? BASE + revealImage : '');
  const [seq, setSeq] = useState([]);
  const [phase, setPhase] = useState('show'); // show | input | wrong | done | exhausted
  const [flash, setFlash] = useState(-1);
  const [pulse, setPulse] = useState(0);
  const [inputPos, setInputPos] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const timers = useRef([]);
  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  const playSequence = (s) => {
    clearTimers();
    setPhase('show'); setInputPos(0); setFlash(-1);
    const SLOT = 620; // цвета переключаются сразу друг за другом, без серого
    const showAll = (startT) => {
      let tt = startT;
      s.forEach((c) => {
        timers.current.push(setTimeout(() => { setFlash(c); setPulse(p => p + 1); }, tt));
        tt += SLOT;
      });
      return tt;
    };
    let end = showAll(500);
    end = showAll(end + 4800); // пауза ~5 сек и повтор 1 раз
    timers.current.push(setTimeout(() => { setFlash(-1); setPhase('input'); }, end + 150));
  };

  useEffect(() => {
    const L = Math.max(2, length || 4);
    let s, tries = 0;
    do {
      s = Array.from({ length: L }, () => Math.floor(Math.random() * 3));
      tries++;
    } while (L >= 3 && new Set(s).size < 3 && tries < 40);
    if (L >= 3 && new Set(s).size < 3) { s[0] = 0; s[1] = 1; s[2] = 2; }
    setSeq(s);
    playSequence(s);
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const press = (c) => {
    if (phase !== 'input') return;
    if (c === seq[inputPos]) {
      const np = inputPos + 1;
      if (np >= seq.length) { setPhase('done'); onSolved(); }
      else setInputPos(np);
    } else {
      const used = attempts + 1;
      setAttempts(used);
      setInputPos(0);
      if (maxAttempts > 0 && used >= maxAttempts) setPhase('exhausted');
      else setPhase('wrong');
    }
  };

  if (phase === 'done') {
    return (
      <div className="mem-wrap">
        <div className="mem-success">{tr('🎉 Верно!')}</div>
        {revealSrc && <img className="mem-reveal-img" src={revealSrc} alt="" />}
        {revealText && <div className="scratch-caption">{revealText}</div>}
        <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Далее →')}</button>
      </div>
    );
  }
  if (phase === 'exhausted') {
    return (
      <div className="mem-wrap">
        <div className="mem-status">{tr('Попытки закончились.')}</div>
        <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Дальше →')}</button>
      </div>
    );
  }

  return (
    <div className="mem-wrap">
      <div className="mem-circle" key={pulse} style={{ background: flash >= 0 ? MEM_COLORS[flash].bg : '#e9edf2' }} />
      <div className="mem-status">
        {phase === 'show' && tr('Запоминайте последовательность…')}
        {phase === 'input' && tr('Повторите по кнопкам: {pos} / {total}', { pos: inputPos, total: seq.length })}
        {phase === 'wrong' && tr('Неверно. Посмотрите ещё раз и повторите.')}
      </div>
      <div className="mem-buttons">
        {MEM_COLORS.map((col, i) => (
          <button key={i} className="mem-btn" style={{ background: col.bg }}
            disabled={phase !== 'input'} onClick={() => press(i)} aria-label={tr(col.name)} />
        ))}
      </div>
      {phase === 'wrong' && (
        <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }}
          onClick={() => playSequence(seq)}>{tr('Показать ещё раз')}</button>
      )}
    </div>
  );
}

// Tap-to-break ice → reveal photo + text
export function IceBreak({ image, caption, taps, onDone }) {
  const src = useOfflineUrl(image ? BASE + image : '');
  const canvasRef = useRef(null);
  const countRef = useRef(0);
  const cracksRef = useRef([]); // накопленные трещины (массив ломаных)
  const [count, setCount] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const need = Math.max(3, parseInt(taps) || 15);

  const paint = (n) => {
    const cv = canvasRef.current; if (!cv || !cv.width) return;
    const ctx = cv.getContext('2d');
    const w = cv.width, h = cv.height;
    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, w, h);
    // Лёд постепенно становится прозрачнее: видно сквозь «туман», ясно — только к концу
    const t = Math.min(1, n / need);
    const alpha = 0.86 - 0.74 * t; // 0.86 → ~0.12
    const g = ctx.createLinearGradient(0, 0, w, h);
    g.addColorStop(0, `rgba(196,220,236,${alpha})`);
    g.addColorStop(0.5, `rgba(232,243,250,${alpha})`);
    g.addColorStop(1, `rgba(186,212,230,${alpha})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
    // морозные разводы
    ctx.fillStyle = `rgba(255,255,255,${0.30 * (1 - t * 0.7)})`;
    for (let i = 0; i < 30; i++) {
      ctx.beginPath();
      ctx.arc(((i * 97) % w), ((i * 53) % h), ((i * 7) % 14) + 4, 0, Math.PI * 2);
      ctx.fill();
    }
    // накопленные трещины
    ctx.strokeStyle = 'rgba(255,255,255,0.95)';
    ctx.lineWidth = 1.6 + 1.4 * t;
    cracksRef.current.forEach(line => {
      ctx.beginPath();
      line.forEach((p, i) => (i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y)));
      ctx.stroke();
    });
    if (n === 0) {
      ctx.fillStyle = '#3a6d8c';
      ctx.font = '600 17px -apple-system, system-ui, sans-serif';
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText(tr('🧊 Бей по льду!'), w / 2, h / 2);
    }
  };

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const id = requestAnimationFrame(() => {
      const w = cv.clientWidth, h = cv.clientHeight;
      if (!w || !h) return;
      cv.width = w; cv.height = h;
      paint(0);
    });
    return () => cancelAnimationFrame(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const crack = (clientX, clientY) => {
    const cv = canvasRef.current; if (!cv || !cv.width) return;
    const r = cv.getBoundingClientRect();
    const x = (clientX - r.left) * (cv.width / r.width);
    const y = (clientY - r.top) * (cv.height / r.height);
    // создаём несколько лучей-трещин из точки удара и запоминаем их
    const branches = 5 + Math.floor(Math.random() * 4);
    for (let b = 0; b < branches; b++) {
      let ang = Math.random() * Math.PI * 2;
      const len = 40 + Math.random() * 90;
      let px = x, py = y;
      const line = [{ x: px, y: py }];
      const segs = 3 + Math.floor(Math.random() * 3);
      for (let s = 0; s < segs; s++) {
        ang += (Math.random() - 0.5) * 0.9;
        px += Math.cos(ang) * (len / segs);
        py += Math.sin(ang) * (len / segs);
        line.push({ x: px, y: py });
      }
      cracksRef.current.push(line);
    }
    const next = countRef.current + 1;
    countRef.current = next;
    setCount(next);
    if (next >= need) setRevealed(true);
    else paint(next);
  };

  return (
    <div className="scratch-wrap">
      <div className="scratch-frame">
        <img className="scratch-img" src={src} alt="" draggable={false} />
        {!revealed && (
          <canvas ref={canvasRef} className="scratch-canvas"
            onPointerDown={(e) => { if (!revealed) crack(e.clientX, e.clientY); }} />
        )}
      </div>
      {revealed && caption && <div className="scratch-caption">{caption}</div>}
      <div className="scratch-hint">
        {revealed ? tr('🎉 Лёд разбит!') : tr('Бей по льду! Осталось ударов: {n}', { n: Math.max(0, need - count) })}
      </div>
      <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Далее →')}</button>
    </div>
  );
}

// Scratch-to-reveal surprise
export function ScratchCard({ image, coverLabel, caption, onDone }) {
  const src = useOfflineUrl(image ? BASE + image : '');
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const cv = canvasRef.current;
    if (!cv) return;
    const id = requestAnimationFrame(() => {
      const w = cv.clientWidth, h = cv.clientHeight;
      if (!w || !h) return;
      cv.width = w; cv.height = h;
      const ctx = cv.getContext('2d');
      ctx.fillStyle = '#a7b2c0';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = '#fff';
      ctx.font = '600 17px -apple-system, system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(coverLabel || tr('Сотрите пальцем 👆'), w / 2, h / 2);
    });
    return () => cancelAnimationFrame(id);
  }, [coverLabel]);

  const scratchAt = (clientX, clientY) => {
    const cv = canvasRef.current; if (!cv || !cv.width) return;
    const r = cv.getBoundingClientRect();
    const x = (clientX - r.left) * (cv.width / r.width);
    const y = (clientY - r.top) * (cv.height / r.height);
    const ctx = cv.getContext('2d');
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(x, y, 28, 0, Math.PI * 2);
    ctx.fill();
  };
  const checkRevealed = () => {
    const cv = canvasRef.current; if (!cv || !cv.width) return;
    const ctx = cv.getContext('2d');
    const data = ctx.getImageData(0, 0, cv.width, cv.height).data;
    let cleared = 0, samples = 0;
    for (let i = 3; i < data.length; i += 320) { samples++; if (data[i] === 0) cleared++; }
    if (samples > 0 && cleared / samples > 0.5) setRevealed(true);
  };
  const onDown = (e) => { drawing.current = true; scratchAt(e.clientX, e.clientY); };
  const onMove = (e) => { if (drawing.current) scratchAt(e.clientX, e.clientY); };
  const onUp = () => { if (drawing.current) { drawing.current = false; checkRevealed(); } };

  return (
    <div className="scratch-wrap">
      <div className="scratch-frame">
        <img className="scratch-img" src={src} alt="" draggable={false} />
        {!revealed && (
          <canvas ref={canvasRef} className="scratch-canvas"
            onPointerDown={onDown} onPointerMove={onMove}
            onPointerUp={onUp} onPointerLeave={onUp} />
        )}
      </div>
      {revealed && caption && <div className="scratch-caption">{caption}</div>}
      <div className="scratch-hint">{revealed ? tr('🎉 Открыто!') : tr('Сотрите слой пальцем')}</div>
      <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Далее →')}</button>
    </div>
  );
}

// "Тогда / Сейчас" — drag a slider to compare two photos
export function BeforeAfter({ before, after, beforeLabel, afterLabel, onDone }) {
  const beforeSrc = useOfflineUrl(before ? BASE + before : '');
  const afterSrc = useOfflineUrl(after ? BASE + after : '');
  const [pos, setPos] = useState(50);
  const frameRef = useRef(null);
  const dragging = useRef(false);

  const setFromX = (clientX) => {
    const el = frameRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    let p = ((clientX - r.left) / r.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  };
  const onDown = (e) => { dragging.current = true; setFromX(e.clientX); };
  const onMove = (e) => { if (dragging.current) setFromX(e.clientX); };
  const onUp = () => { dragging.current = false; };

  return (
    <div className="ba-wrap">
      <div className="ba-frame" ref={frameRef}
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}>
        <img className="ba-img" src={afterSrc} alt="" draggable={false} />
        <img className="ba-img ba-img-top" src={beforeSrc} alt="" draggable={false}
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
        {beforeLabel && <span className="ba-label ba-label-left">{beforeLabel}</span>}
        {afterLabel && <span className="ba-label ba-label-right">{afterLabel}</span>}
        <div className="ba-divider" style={{ left: `${pos}%` }}>
          <div className="ba-knob">‹ ›</div>
        </div>
      </div>
      <div className="ba-hint">{tr('Двигайте ползунок, чтобы сравнить')}</div>
      <button className="swipe-card-btn yes" style={{ width: '100%', marginTop: 14 }} onClick={onDone}>{tr('Далее →')}</button>
    </div>
  );
}

export function SwipeGallery({ images, baseUrl, onImageTap }) {
  const [index, setIndex] = useState(0);
  const [resolvedUrls, setResolvedUrls] = useState([]);
  const touchStart = useRef(0);
  const touchEnd = useRef(0);

  useEffect(() => {
    Promise.all(images.map(url => getOfflineUrl(baseUrl + url))).then(setResolvedUrls);
  }, [images, baseUrl]);

  const onTouchStart = (e) => { touchStart.current = e.touches[0].clientX; };
  const onTouchMove = (e) => { touchEnd.current = e.touches[0].clientX; };
  const onTouchEnd = () => {
    const diff = touchStart.current - touchEnd.current;
    if (diff > 50 && index < images.length - 1) setIndex(index + 1);
    if (diff < -50 && index > 0) setIndex(index - 1);
  };

  const urls = resolvedUrls.length > 0 ? resolvedUrls : images.map(u => baseUrl + u);

  return (
    <div className="swipe-gallery">
      <div
        className="swipe-gallery-track"
        style={{ transform: `translateX(-${index * 100}%)` }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {urls.map((url, i) => (
          <img key={i} src={url} alt="" className="swipe-gallery-img" onClick={() => onImageTap && onImageTap(urls, i)} />
        ))}
      </div>
      {images.length > 1 && (
        <div className="swipe-gallery-dots">
          {images.map((_, i) => (
            <div key={i} className={`swipe-dot ${i === index ? 'active' : ''}`} onClick={() => setIndex(i)} />
          ))}
        </div>
      )}
    </div>
  );
}

